(() => {
  'use strict';

  const RELEASE = 'home-content-v1';
  const HOME_PATH = /^\/(?:$|(?:en|zh)\/?$)/;
  if (window.__gpContentTrackingLoaded || !document.body.classList.contains('hdt-page-type-index') || !HOME_PATH.test(location.pathname)) return;
  window.__gpContentTrackingLoaded = true;

  const PUBLIC_PATH = /^\/(?:collections|products|pages|blogs|search|cart|policies)(?:\/|$)/;
  const ACTIONS = 'a[href],button,[role="button"]';
  const pageview = () => crypto.randomUUID();
  let pageviewId = pageview();
  let observer;
  let boundarySlots = new WeakMap();
  let trackedElements = new WeakMap();
  const timers = new Map();
  const tracked = new Map();
  const impressed = new Set();
  const pendingImpressions = new Set();
  const sectionPositions = new Map();
  const recentActivations = new Map();

  const hash = (value) => {
    let current = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      current ^= value.charCodeAt(index);
      current = Math.imul(current, 16777619);
    }
    return (current >>> 0).toString(16).padStart(8, '0');
  };

  const key = (value, fallback) => {
    const normalized = String(value || '').toLowerCase().replace(/[^a-z0-9:_-]+/g, '_')
      .replace(/^_+|_+$/g, '').slice(0, 80);
    return /^[a-z0-9]/.test(normalized) ? normalized : fallback;
  };

  const locale = () => {
    const lang = document.documentElement.lang.toLowerCase();
    return lang.startsWith('zh') ? 'zh' : lang.startsWith('en') ? 'en' : 'unknown';
  };

  const device = () => innerWidth < 750 ? 'mobile' : innerWidth < 1024 ? 'tablet' : 'desktop';

  const pathsFor = (raw) => {
    try {
      const url = new URL(raw, location.origin);
      if (url.origin !== location.origin || url.pathname.includes('//')) return null;
      const canonical = url.pathname.replace(/^\/(?:en|zh)(?=\/|$)/, '') || '/';
      if (canonical !== '/' && !PUBLIC_PATH.test(canonical)) return null;
      return { localized: url.pathname, canonical };
    } catch { return null; }
  };

  const safeMediaPath = (raw) => {
    if (!raw) return '';
    try {
      const url = new URL(raw, location.origin);
      const host = url.hostname.replace(/^www\./, '');
      const ownHost = location.hostname.replace(/^www\./, '');
      if (host !== ownHost && host !== 'cdn.shopify.com') return '';
      return url.pathname.slice(0, 512);
    } catch { return ''; }
  };

  const externalFor = (raw) => {
    const text = String(raw || '').trim().toLowerCase();
    if (text.startsWith('tel:')) return { type: 'contact', identity: 'phone' };
    if (text.startsWith('mailto:')) return { type: 'contact', identity: 'email' };
    try {
      const host = new URL(text, location.origin).hostname.replace(/^www\./, '');
      if (host === 'wa.me' || host === 'api.whatsapp.com') return { type: 'contact', identity: 'whatsapp' };
      if (host === 'facebook.com' || host === 'm.facebook.com') return { type: 'external', identity: 'facebook' };
      if (host === 'instagram.com') return { type: 'external', identity: 'instagram' };
      return host !== location.hostname.replace(/^www\./, '') ? { type: 'external', identity: 'external_link' } : null;
    } catch { return null; }
  };

  const gid = (value, resource) => {
    const text = String(value || '');
    if (new RegExp(`^gid://shopify/${resource}/[1-9]\\d*$`).test(text)) return text;
    return /^[1-9]\d*$/.test(text) ? `gid://shopify/${resource}/${text}` : null;
  };

  const sectionFor = (element) => {
    const explicit = element.closest('[data-gp-track-section]');
    if (explicit) return {
      explicit: true,
      element: explicit,
      key: key(`home_${explicit.dataset.gpTrackSection}`, 'home_content'),
      versionSource: explicit.dataset.gpTrackVersionSource || RELEASE,
    };
    if (element.closest('header, .shopify-section-group-header-group')) {
      return { explicit: false, element: document.querySelector('header') || document.body, key: 'header', versionSource: RELEASE };
    }
    if (element.closest('footer, .shopify-section-group-footer-group')) {
      return { explicit: false, element: document.querySelector('footer') || document.body, key: 'footer', versionSource: RELEASE };
    }
    const shopifySection = element.closest('[id^="shopify-section-"]');
    const suffix = shopifySection?.id.split('__').pop()?.replace(/^template--\d+/, 'content');
    return { explicit: false, element: shopifySection || document.querySelector('main') || document.body, key: key(`home_${suffix || 'content'}`, 'home_content'), versionSource: RELEASE };
  };

  const metadataFor = (element) => {
    if (!(element instanceof Element) || element.matches('[disabled],[aria-disabled="true"]')) return null;
    const section = sectionFor(element);
    const href = element instanceof HTMLAnchorElement ? element.getAttribute('href') : null;
    const target = href ? pathsFor(href) : null;
    let account = false;
    if (href && target === null) {
      try {
        const url = new URL(href, location.origin);
        account = url.origin === location.origin && /^\/(?:(?:en|zh)\/)?account(?:\/login)?\/?$/u.test(url.pathname);
      } catch { /* invalid link */ }
    }
    const outside = href && target === null && !account ? externalFor(href) : null;
    const newsletter = Boolean(element.closest('form[action*="contact"], [class*="newsletter"]'));
    const contentType = newsletter ? 'action'
      : outside?.type || (target?.canonical.includes('/products/') ? 'product'
        : element.closest('header,footer') ? 'navigation'
          : element instanceof HTMLAnchorElement ? 'link' : 'action');
    if (href && target === null && outside === null && !account) return null;

    const boundary = element.closest('[data-gp-track-content], article, .hdt-card-product, li') || element;
    let position = boundarySlots.get(boundary);
    if (position === undefined) {
      position = (sectionPositions.get(section.key) || 0) + 1;
      if (position > 500) return null;
      sectionPositions.set(section.key, position);
      boundarySlots.set(boundary, position);
    }
    const explicitSlot = element.closest('[data-gp-track-slot]')?.getAttribute('data-gp-track-slot');
    const slotKey = key(explicitSlot || `slot_${position}`, `slot_${position}`);
    const productElement = element.closest('[data-gp-track-product-id], [data-product-id]');
    const productId = gid(productElement?.getAttribute('data-gp-track-product-id') || productElement?.getAttribute('data-product-id'), 'Product');
    let variantId = gid(element.closest('[data-gp-track-variant-id]')?.getAttribute('data-gp-track-variant-id'), 'ProductVariant');
    if (variantId === null && target && productId) {
      try { variantId = gid(new URL(href, location.origin).searchParams.get('variant'), 'ProductVariant'); } catch { /* no variant */ }
    }
    if (productId === null) variantId = null;
    const actionIdentity = newsletter ? 'newsletter_submit' : account ? 'account' : outside?.identity || target?.canonical || `button_${position}`;
    const contentPrefix = contentType === 'action' || contentType === 'contact' || contentType === 'external'
      ? key(actionIdentity, contentType)
      : contentType;
    const identity = `${section.key}|${slotKey}|${contentType}|${productId || ''}|${variantId || ''}|${actionIdentity}`;
    const sectionCopy = productId === null && section.explicit
      ? section.element.querySelector('h1,h2,.gp-home__heading,.gp-home__intro,p')
      : null;
    const sectionImage = productId === null && section.explicit ? section.element.querySelector('img') : null;
    const imagePath = section.explicit
      ? safeMediaPath(
        element.querySelector('img')?.currentSrc || element.querySelector('img')?.src
          || sectionImage?.currentSrc || sectionImage?.src || '',
      )
      : '';
    // Text is read only for our controlled homepage sections and is hashed locally. Raw copy is
    // never published; generic header, footer, search and newsletter content is never inspected.
    const controlledText = section.explicit
      ? `${String(element.textContent || '').trim()}|${String(sectionCopy?.textContent || '').trim()}`.replace(/\s+/g, ' ').slice(0, 300)
      : '';
    const localVersion = element.closest('[data-gp-track-version-source]')?.getAttribute('data-gp-track-version-source') || '';
    return {
      dedupeKey: identity,
      observe: element,
      data: {
        pageviewId,
        sectionKey: section.key,
        contentKey: `${contentPrefix}_${hash(`${section.key}|${productId || actionIdentity}`)}`,
        contentVersion: hash(`${section.versionSource}|${localVersion}|${identity}|${imagePath}|${controlledText}`),
        slotKey,
        position,
        contentType,
        targetPath: target?.localized || null,
        productId,
        variantId,
        locale: locale(),
        deviceBucket: device(),
      },
    };
  };

  const publishWhenReady = (name, metadata, onAccepted, attempt = 0) => {
    const retry = () => {
      if (attempt < 20) setTimeout(() => publishWhenReady(name, metadata, onAccepted, attempt + 1), 100);
      else onAccepted?.(false);
    };
    const analytics = window.Shopify?.analytics;
    if (typeof analytics?.publish === 'function') {
      try {
        const result = analytics.publish(name, { ...metadata, deviceBucket: device() });
        if (result === false) return retry();
        if (result && typeof result.then === 'function') {
          result.then((accepted) => accepted === false ? retry() : onAccepted?.(true)).catch(retry);
          return;
        }
        onAccepted?.(true);
        return;
      } catch { /* SDK may still be initializing. */ }
    }
    retry();
  };

  const isReachablyVisible = (element) => {
    if (document.visibilityState !== 'visible' || element.getClientRects().length === 0) return false;
    for (let current = element; current instanceof Element; current = current.parentElement) {
      const style = getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    const rect = element.getBoundingClientRect();
    let left = Math.max(rect.left, 0);
    let right = Math.min(rect.right, innerWidth);
    let top = Math.max(rect.top, 0);
    let bottom = Math.min(rect.bottom, innerHeight);
    for (let current = element.parentElement; current instanceof Element; current = current.parentElement) {
      const style = getComputedStyle(current);
      const clipped = /^(?:auto|hidden|clip|scroll)$/u;
      const clipX = clipped.test(style.overflowX) || clipped.test(style.overflow);
      const clipY = clipped.test(style.overflowY) || clipped.test(style.overflow);
      if (!clipX && !clipY) continue;
      const ancestor = current.getBoundingClientRect();
      if (clipX) {
        left = Math.max(left, ancestor.left);
        right = Math.min(right, ancestor.right);
      }
      if (clipY) {
        top = Math.max(top, ancestor.top);
        bottom = Math.min(bottom, ancestor.bottom);
      }
    }
    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);
    const visibleArea = width * height;
    const reachableArea = Math.min(Math.max(1, rect.width * rect.height), Math.max(1, innerWidth * innerHeight));
    return visibleArea / reachableArea >= 0.5;
  };

  const startImpression = (metadata) => {
    const id = metadata.dedupeKey;
    clearTimeout(timers.get(metadata.observe));
    timers.delete(metadata.observe);
    if (impressed.has(id) || pendingImpressions.has(id) || !isReachablyVisible(metadata.observe)) return;
    timers.set(metadata.observe, setTimeout(() => {
      timers.delete(metadata.observe);
      if (impressed.has(id) || pendingImpressions.has(id) || !isReachablyVisible(metadata.observe)) return;
      pendingImpressions.add(id);
      publishWhenReady('gp_content_impression', metadata.data, (accepted) => {
        pendingImpressions.delete(id);
        if (accepted) impressed.add(id);
      });
    }, 1_000));
  };

  const observe = () => {
    observer?.disconnect();
    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const metadata = entry.target.__gpContentMetadata;
        if (!metadata) continue;
        if (!entry.isIntersecting || document.visibilityState !== 'visible') {
          clearTimeout(timers.get(metadata.observe));
          timers.delete(metadata.observe);
        } else startImpression(metadata);
      }
    }, { threshold: [0, 0.5] });
    for (const instances of tracked.values()) for (const metadata of instances) observer.observe(metadata.observe);
  };

  const register = (element) => {
    const known = trackedElements.get(element);
    if (known) return known;
    const metadata = metadataFor(element);
    if (!metadata) return null;
    metadata.observe.__gpContentMetadata = metadata;
    trackedElements.set(element, metadata);
    const instances = tracked.get(metadata.dedupeKey) || new Set();
    instances.add(metadata);
    tracked.set(metadata.dedupeKey, instances);
    observer?.observe(metadata.observe);
    return metadata;
  };

  const scan = (root = document) => {
    if (root instanceof Element && root.matches(ACTIONS)) register(root);
    root.querySelectorAll?.(ACTIONS).forEach(register);
  };

  const recheck = (root) => {
    const elements = [];
    if (root instanceof Element && root.matches(ACTIONS)) elements.push(root);
    root.querySelectorAll?.(ACTIONS).forEach((element) => elements.push(element));
    for (const element of elements) {
      const metadata = register(element);
      if (metadata) startImpression(metadata);
    }
  };

  const resetPageview = () => {
    pageviewId = pageview();
    timers.forEach(clearTimeout);
    timers.clear();
    tracked.clear();
    trackedElements = new WeakMap();
    impressed.clear();
    pendingImpressions.clear();
    recentActivations.clear();
    sectionPositions.clear();
    boundarySlots = new WeakMap();
    observe();
    scan();
  };

  const publishActivation = (element, source) => {
    const metadata = register(element);
    if (!metadata) return;
    const now = performance.now();
    const recent = recentActivations.get(metadata.dedupeKey);
    if (source === 'submit' && recent && now - recent < 500) return;
    recentActivations.set(metadata.dedupeKey, now);
    setTimeout(() => {
      if (recentActivations.get(metadata.dedupeKey) === now) recentActivations.delete(metadata.dedupeKey);
    }, 500);
    publishWhenReady('gp_content_click', metadata.data);
  };

  document.addEventListener('click', (event) => {
    const element = event.target instanceof Element ? event.target.closest(ACTIONS) : null;
    if (element) publishActivation(element, 'click');
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    const element = form?.querySelector('button[type="submit"], input[type="submit"]');
    if (element) publishActivation(element, 'submit');
  }, true);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      timers.forEach(clearTimeout);
      timers.clear();
      return;
    }
    for (const instances of tracked.values()) for (const metadata of instances) startImpression(metadata);
  });

  let recheckTimer;
  const scheduleRecheck = (root) => {
    clearTimeout(recheckTimer);
    recheckTimer = setTimeout(() => recheck(root), 50);
  };
  new MutationObserver((records) => {
    for (const record of records) {
      record.addedNodes.forEach((node) => { if (node instanceof Element) scan(node); });
      if (record.type === 'attributes' && record.target instanceof Element) scheduleRecheck(record.target);
    }
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden'],
    childList: true,
    subtree: true,
  });
  document.addEventListener('transitionend', (event) => {
    if (event.target instanceof Element) scheduleRecheck(event.target);
  }, true);

  window.addEventListener('pageshow', (event) => { if (event.persisted) resetPageview(); });
  observe();
  scan();
})();
